import { useState } from "react";
import { Link } from "react-router-dom";

import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Tooltip from "@mui/material/Tooltip";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ProtectedButton from "authentication/ProtectedButton";

import ConfirmationDialog from "../RemoveUserDialog";

import { deleteUser, suspendUser, inviteUser } from "api/UserApi";
import { useAuth0 } from "@auth0/auth0-react";
import { PERMISSIONS } from "constants/portal";
import { UserRoleDialog } from "../UserRoleDialog";
import { useIsOwner } from "hooks/useIsOwner";

const ITEM_HEIGHT = 48;

export default function LongMenu({
  auth0Id,
  username,
  openTransferDialog,
  roleName,
  tenantId,
  getUsers,
  status,
  currentUser,
  actionOrientation
}) {
  const isOwner = useIsOwner();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [openRemoveUserDialog, setOpenRemoveUserDialog] = useState(false);
  const [openReinviteDialog, setOpenReinviteDialog] = useState(false);
  const [openSuspendUserDialog, setOpenSuspendUserDialog] = useState(false);
  const [openUnsuspendUserDialog, setOpenUnsuspendUserDialog] = useState(false);
  const [openUserRoleDialog, setOpenUserRoleDialog] = useState(false);
  const [isDoingAction, setIsDoingAction] = useState(false);
  const { user } = useAuth0();

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleCloseRemoveUserDialog = () => {
    setOpenRemoveUserDialog(false);
    setAnchorEl(null);
  };
  const handleCloseSuspendUserDialog = () => {
    setOpenSuspendUserDialog(false);
    setAnchorEl(null);
  };
  const handleCloseUnsuspendUserDialog = () => {
    setOpenUnsuspendUserDialog(false);
    setAnchorEl(null);
  };
  const handleOpenRemoveUserDialog = () => {
    setOpenRemoveUserDialog(true);
  };
  const handleSuspendUserDialog = () => {
    setOpenSuspendUserDialog(true);
  };
  const handleUnsuspendUserDialog = () => {
    setOpenUnsuspendUserDialog(true);
  };
  const handleOpenReinviteDialog = () => {
    setOpenReinviteDialog(true);
  };
  const handleCloseReinviteDialog = () => {
    setOpenReinviteDialog(false);
  };
  const handleReinvite = async (onDialogClose) => {
    if (isDoingAction) return;

    setIsDoingAction(true);
    const emailAdd = currentUser?.email_address;
    const currentRole = currentUser?.role_id;

    try {
      const response = await inviteUser(currentUser?.tenant_id, {
        emails: [emailAdd],
        inviter: user?.cxfUserId,
        messages: "",
        roles: { [emailAdd]: `${currentRole}` }
      });

      handleCloseMenu();
      if (response?.isSuccessful && response?.resultObject?.length > 0) {
        toast.success(response.message, 2500);
        await getUsers();
        onDialogClose?.();

        return
      } else if (!response?.isSuccessful) {
        toast.warning(response?.message || "Unable to resend invitation. Please try again.");

        return
      }
      toast.error(response?.message || "Unable to resend invitation. Please try again.");
    } catch (error) {
      console.log(error);
      toast.error("Unable to resend invitation. Please try again.");
    } finally {
      setIsDoingAction(false);
    }
  };
  const handleRemove = async () => {
    if (isDoingAction) return;
    setIsDoingAction(true);
    try {
      const result = await deleteUser(tenantId, auth0Id);
      if (!result?.isSuccessful) {
        toast.error(result?.message || "Unable to remove user. Please try again.");
        return;
      }
      toast.success("User removed successfully!");
      await getUsers();
      handleCloseRemoveUserDialog();
    } catch (error) {
      console.log(error);
      toast.error("Unable to remove user. Please try again.");
    } finally {
      setIsDoingAction(false);
    }
  };

  const handleSuspend = async () => {
    try {
      const suspendResponse = await suspendUser(tenantId, auth0Id, "suspended");
      if (suspendResponse) {
        toast.success("User suspended successfully!");
      }
      getUsers();
      handleCloseSuspendUserDialog();
    } catch (error) {
      console.log(error);
    }
  };

  const handleUnsuspend = async () => {
    try {
      const suspendResponse = await suspendUser(tenantId, auth0Id, "active");
      if (suspendResponse) {
        toast.success("User unsuspended successfully!");
      }
      getUsers();
      handleCloseUnsuspendUserDialog();
    } catch (error) {
      console.log(error);
    }
  };

  const handleToggleUserRoleDialog = (isOpen) => () => {
    handleCloseMenu();
    setOpenUserRoleDialog(isOpen);
  };

  return (
    <div>
      <ProtectedButton permissionName="manageUserMembership">
        <IconButton
          aria-label="more"
          id="long-button"
          aria-controls={open ? "long-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
          onClick={handleOpenMenu}
        >
          <Tooltip title="More Actions" placement="top">
            {actionOrientation === "vertical" ? <MoreVertIcon /> : <MoreHorizIcon />}
          </Tooltip>
        </IconButton>
      </ProtectedButton>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button"
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        PaperProps={{
          style: {
            maxHeight: ITEM_HEIGHT * 4.5
          }
        }}
      >
        {roleName?.toLowerCase() !== "owner" && status === "invited" ? (
          <ProtectedButton permissionName={PERMISSIONS.INVITE_USERS}>
            <MenuItem onClick={handleOpenReinviteDialog}>Reinvite</MenuItem>
          </ProtectedButton>
        ) : (
          <ProtectedButton permissionName={PERMISSIONS.EDIT_USERS} isOwner={isOwner}>
            <Link to={`/account/users/user-edit/${auth0Id}`}>
              <MenuItem>Edit</MenuItem>
            </Link>
          </ProtectedButton>
        )}
        {user?.cxfUserId === auth0Id || roleName?.toLowerCase() === "owner" ? null : (
          <ProtectedButton permissionName={PERMISSIONS.REMOVE_USERS}>
            <MenuItem onClick={handleOpenRemoveUserDialog}>Remove</MenuItem>
          </ProtectedButton>
        )}
        {user?.cxfUserId !== auth0Id &&
          roleName?.toLowerCase() !== "owner" &&
          status === "active" && (
            <ProtectedButton permissionName={PERMISSIONS.SUSPEND_USERS}>
              <MenuItem onClick={handleSuspendUserDialog}>Suspend</MenuItem>
            </ProtectedButton>
          )}
        {isOwner && roleName?.toLowerCase() !== "owner" && status === "active" && (
          <ProtectedButton permissionName={PERMISSIONS.TRANSFER_OWNERSHIP}>
            <MenuItem onClick={() => openTransferDialog(username, auth0Id)}>
              Transfer Ownership
            </MenuItem>
          </ProtectedButton>
        )}
        {isOwner && roleName?.toLowerCase() !== "owner" && (
          <ProtectedButton permissionName={PERMISSIONS.ASSIGN_ROLE}>
            <MenuItem onClick={handleToggleUserRoleDialog(true)}>Assign Role</MenuItem>
          </ProtectedButton>
        )}

        {roleName?.toLowerCase() !== "owner" && status === "suspended" && (
          <ProtectedButton permissionName={PERMISSIONS.UNSUSPEND_USERS}>
            <MenuItem onClick={handleUnsuspendUserDialog}>Unsuspend</MenuItem>
          </ProtectedButton>
        )}
      </Menu>
      <ConfirmationDialog
        open={openRemoveUserDialog}
        onClose={handleCloseRemoveUserDialog}
        onAccept={handleRemove}
        acceptButtonText="Remove"
        dialogTitle="Remove User"
        dialogContent={["Are you sure you want to remove ", currentUser?.email_address || currentUser?.first_name]}
        acceptButtonColor="info"
        waitAccept
        isLoading={isDoingAction}
      />
      <ConfirmationDialog
        open={openSuspendUserDialog}
        onClose={handleCloseSuspendUserDialog}
        onAccept={handleSuspend}
        acceptButtonText="Suspend"
        dialogTitle="Suspend User"
        dialogContent={["Are you sure you want to suspend ", currentUser?.first_name]}
        acceptButtonColor="info"
      />
      <ConfirmationDialog
        open={openUnsuspendUserDialog}
        onClose={handleCloseUnsuspendUserDialog}
        onAccept={handleUnsuspend}
        acceptButtonText="Unsuspend"
        dialogTitle="Unsuspend User"
        dialogContent={["Are you sure you want to unsuspend ", currentUser?.first_name]}
        acceptButtonColor="info"
      />
      <ConfirmationDialog
        open={openReinviteDialog}
        onClose={handleCloseReinviteDialog}
        onAccept={handleReinvite}
        acceptButtonText="Reinvite"
        dialogTitle="Reinvite User"
        dialogContent={["Resend the invitation to ", currentUser?.email_address || currentUser?.first_name]}
        acceptButtonColor="info"
        waitAccept
        isLoading={isDoingAction}
      />

      <UserRoleDialog
        onClose={handleToggleUserRoleDialog(false)}
        open={openUserRoleDialog}
        currentUser={currentUser}
      />
    </div>
  );
}
